import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import axios from 'axios';
import { useSubscriptionStore, useUserStore } from '../../store';
import { formatPriceWithCurrency } from '../../utils/vat';

function ExtraWorkRequestDialog({ open, handleClose, maintenance, setMaintenance }) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const showInclVat = currentOrganization?.default_price_includes_vat ?? false;
  const { subscriptionInfo, fetchSubscriptionInfo, canUseSms } = useSubscriptionStore();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pendingItems, setPendingItems] = useState({ parts: [], jobs: [] });
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [language, setLanguage] = useState(maintenance?.customer?.preferred_language || 'fi');
  const [result, setResult] = useState(null);

  // Check if SMS is available (not available during trial)
  const smsEnabled = canUseSms();
  const isTrial = subscriptionInfo?.is_trial;

  useEffect(() => {
    if (open && maintenance?.id) {
      fetchPendingItems();
      // Pre-fill contact info
      setEmail(maintenance.customer?.email || '');
      setPhone(maintenance.customer?.phone || '');
      setLanguage(maintenance.customer?.preferred_language || 'fi');
      setResult(null);
      setMessage('');
      // Fetch subscription info if not already loaded
      if (!subscriptionInfo) {
        fetchSubscriptionInfo();
      }
    }
  }, [open, maintenance?.id]);

  const fetchPendingItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `/api/maintenances/${maintenance.id}/pending_confirmation_items/`
      );
      setPendingItems(response.data);
      // Pre-select all items
      setSelectedParts(response.data.parts.map(p => p.id));
      setSelectedJobs(response.data.jobs.map(j => j.id));
    } catch (err) {
      console.error('Error fetching pending items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const response = await axios.post(
        `/api/maintenances/${maintenance.id}/create_extra_work_request/`,
        {
          part_ids: selectedParts,
          job_ids: selectedJobs,
          message,
          send_email: sendEmail,
          send_sms: sendSms,
          email: email || undefined,
          phone: phone || undefined,
          language,
        }
      );
      setResult(response.data);
      setMaintenance(response.data.maintenance);
    } catch (err) {
      setResult({ error: err.response?.data?.error || t('Error sending request') });
    } finally {
      setSending(false);
    }
  };

  const calculateTotal = () => {
    let total = 0;
    const totalField = showInclVat ? 'line_total_incl_vat' : 'line_total';
    pendingItems.parts
      .filter(p => selectedParts.includes(p.id))
      .forEach(p => { total += parseFloat(p[totalField]) || 0; });
    pendingItems.jobs
      .filter(j => selectedJobs.includes(j.id))
      .forEach(j => { total += parseFloat(j[totalField]) || 0; });
    return total;
  };

  const handleCancel = () => {
    setResult(null);
    setMessage('');
    handleClose();
  };

  const hasSelections = selectedParts.length > 0 || selectedJobs.length > 0;
  const canSend = hasSelections && (sendEmail || sendSms) && (!sendEmail || email) && (!sendSms || phone);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HourglassEmptyIcon sx={{ color: 'warning.main' }} />
          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
            {t('Request customer approval')}
          </Typography>
        </Box>
        <IconButton onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
              {t('Select items that require customer approval and send the request via email or SMS.')}
            </Alert>

            {/* Items List */}
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              {t('Items requiring approval')}
            </Typography>

            <List dense sx={{ bgcolor: 'action.hover', borderRadius: '8px', mb: 3 }}>
              {pendingItems.parts.map(part => (
                <ListItem key={`part-${part.id}`} sx={{ py: 1 }}>
                  <Checkbox
                    checked={selectedParts.includes(part.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedParts([...selectedParts, part.id]);
                      } else {
                        setSelectedParts(selectedParts.filter(id => id !== part.id));
                      }
                    }}
                    size="small"
                  />
                  <ListItemText
                    primary={part.name}
                    secondary={`${part.quantity} × ${formatPriceWithCurrency(showInclVat ? part.effective_price_incl_vat : part.effective_price)} (${showInclVat ? t('incl. VAT') : t('excl. VAT')})`}
                  />
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatPriceWithCurrency(showInclVat ? part.line_total_incl_vat : part.line_total)}
                  </Typography>
                </ListItem>
              ))}
              {pendingItems.jobs.map(job => (
                <ListItem key={`job-${job.id}`} sx={{ py: 1 }}>
                  <Checkbox
                    checked={selectedJobs.includes(job.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedJobs([...selectedJobs, job.id]);
                      } else {
                        setSelectedJobs(selectedJobs.filter(id => id !== job.id));
                      }
                    }}
                    size="small"
                  />
                  <ListItemText
                    primary={job.name}
                    secondary={`${job.quantity} × ${formatPriceWithCurrency(showInclVat ? job.effective_price_incl_vat : job.effective_price)} (${showInclVat ? t('incl. VAT') : t('excl. VAT')})`}
                  />
                  <Typography sx={{ fontWeight: 600 }}>
                    {formatPriceWithCurrency(showInclVat ? job.line_total_incl_vat : job.line_total)}
                  </Typography>
                </ListItem>
              ))}
              {pendingItems.parts.length === 0 && pendingItems.jobs.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary={t('No items awaiting confirmation')}
                    sx={{ textAlign: 'center', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>

            {/* Total */}
            {hasSelections && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography sx={{ fontWeight: 600 }}>{t('Total')} ({showInclVat ? t('incl. VAT') : t('excl. VAT')})</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>
                  {formatPriceWithCurrency(calculateTotal())}
                </Typography>
              </Box>
            )}

            {/* Message */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('Message to customer (optional)')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('E.g., During inspection we found worn brake pads...')}
              sx={{ mb: 3 }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Send Options */}
            <Typography sx={{ fontWeight: 600, mb: 2 }}>{t('Send notification')}</Typography>

            {/* Language selector */}
            <LanguageSelect
              size="small"
              sx={{ mb: 2, minWidth: 200, display: 'block' }}
              value={language}
              label={t('Message language')}
              onChange={(e) => setLanguage(e.target.value)}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 20 }} />
                  <Typography>{t('Send via email')}</Typography>
                </Box>
              }
            />
            {sendEmail && (
              <TextField
                fullWidth
                size="small"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('Email address')}
                sx={{ mb: 2, ml: 4, width: 'calc(100% - 32px)' }}
              />
            )}

            <Tooltip
              title={!smsEnabled && isTrial ? t('SMS is not available during trial') : ''}
              placement="right"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendSms}
                    onChange={(e) => setSendSms(e.target.checked)}
                    disabled={!smsEnabled}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmsIcon sx={{ fontSize: 20, color: smsEnabled ? 'inherit' : 'text.disabled' }} />
                    <Typography sx={{ color: smsEnabled ? 'inherit' : 'text.disabled' }}>
                      {t('Send via SMS')}
                    </Typography>
                    {!smsEnabled && isTrial && (
                      <Tooltip title={t('SMS is not available during trial')}>
                        <InfoIcon sx={{ fontSize: 16, color: 'info.main', ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </Box>
                }
              />
            </Tooltip>
            {!smsEnabled && isTrial && (
              <Alert severity="info" sx={{ mb: 2, ml: 4, width: 'calc(100% - 32px)' }}>
                {t('SMS notifications are available with a paid subscription. Upgrade to send SMS messages to customers.')}
              </Alert>
            )}
            {sendSms && smsEnabled && (
              <TextField
                fullWidth
                size="small"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('Phone number')}
                sx={{ mb: 2, ml: 4, width: 'calc(100% - 32px)' }}
              />
            )}

            {/* Result */}
            {result && (
              <Box sx={{ mt: 2 }}>
                {result.error && (
                  <Alert severity="error">{result.error}</Alert>
                )}
                {result.email_sent && (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    {t('Email sent successfully')}
                  </Alert>
                )}
                {result.sms_sent && (
                  <Alert severity="success">
                    {t('SMS sent successfully')}
                  </Alert>
                )}
                {(result.email_sent || result.sms_sent) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    {t('The customer will receive a link to approve or decline the request.')}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleCancel}>{t('Close')}</Button>
        {!result?.email_sent && !result?.sms_sent && (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !canSend}
            sx={{ bgcolor: 'warning.main', '&:hover': { bgcolor: 'warning.dark' } }}
          >
            {sending ? <CircularProgress size={20} color="inherit" /> : <span>{t('Send request')}</span>}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ExtraWorkRequestDialog;
